import React, { useState, useCallback, memo } from "react";
import Form from "react-bootstrap/Form";
import {
    DRFormGrid, DRHint,
    DRFormTitle, DRFormBadge,
    DRCheckboxGrid,
    AMTSLItem, AMTSLCheckIcon, AMTSLLabel, AMTSLProgress,

    DRButtonRow, DRPrimaryBtn, DRSecondaryBtn,
} from "./DeliveryRecordStyles";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import Label from "../../../Utils/FormInputs/Label";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";

/* Helper: convert { value, label } arrays to { value, text } for SelectInput */
const toSelectOptions = (items) => items.map((m) => ({ value: m.value, text: m.label }));

/* Boolean select options */
const PLACENTA_COMPLETE_OPTIONS = [
    { value: "true", text: "Yes — Complete" },
    { value: "false", text: "No — Incomplete" },
];

const NUMBER_OF_BABIES_OPTIONS = [
    { value: "1", text: "1" },
    { value: "2", text: "2" },
    { value: "3", text: "3" },
    { value: "4", text: "4" },
    { value: "5", text: "5" },
    { value: "6", text: "6" },
];

const YES_NO_OPTIONS = [
    { value: "false", text: "No" },
    { value: "true", text: "Yes" },
];

/* ── Memoized Complications Section ── */
const ComplicationsSection = memo(({ complications, options, toggleComplication, collapsed, toggleSection }) => {
    const selected = complications || [];
    return (
        <div>
            <div
                style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}
                onClick={() => toggleSection("complications")}
            >
                <DRFormTitle>Complications</DRFormTitle>
                {selected.length > 0
                    ? <span>{selected.length} selected</span>
                    : <span style={{ fontSize: "0.8rem", color: "#999" }}>{collapsed ? "▼" : "▲"}</span>}
            </div>
            {!collapsed && (
                <Form.Group>
                    <DRCheckboxGrid cols={3}>
                        {options.map((opt) => (
                            <Form.Check
                                key={opt.value}
                                type="checkbox"
                                label={opt.label}
                                checked={selected.indexOf(opt.value) >= 0}
                                onChange={() => toggleComplication(opt.value)}
                            />
                        ))}
                    </DRCheckboxGrid>
                </Form.Group>
            )}
        </div>
    );
});

export const DeliveryRecordForm = ({ dr, doctorName }) => {
    const { form, handleChange } = dr;

    const [collapsed, setCollapsed] = useState({
        stages: false, thirdstage: false, perineal: false,
        complications: false, condition: false,
    });

    const toggleSection = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    /* ── Section 1: Mode of Delivery ── */
    const renderModeSection = () => (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <DRFormTitle>Mode of Delivery</DRFormTitle>
                {form.delivery_mode
                    ? <DRFormBadge variant="success">Selected</DRFormBadge>
                    : <DRFormBadge variant="warning">Required</DRFormBadge>}
            </div>
            <DRFormGrid cols={3}>
                <Form.Group>
                    <Label name="Delivery Date & Time" type />
                    <TextField
                        type="datetime-local"
                        name="delivery_datetime"
                        value={form.delivery_datetime || ""}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Mode of Delivery" type />
                    <SelectInput
                        name="delivery_mode"
                        value={form.delivery_mode || ""}
                        onChange={handleChange}
                        options={toSelectOptions(dr.DELIVERY_MODES)}
                        defaultOption="— Select mode —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Number of Babies" />
                    <SelectInput
                        name="number_of_babies"
                        value={form.number_of_babies || "1"}
                        onChange={handleChange}
                        options={NUMBER_OF_BABIES_OPTIONS}
                    />
                </Form.Group>
            </DRFormGrid>
            {dr.isOperative && (
                <div style={{ marginTop: "12px" }}>
                    <Form.Group>
                        <Label name="Indication for Operative Delivery" type />
                        <ResizableTextarea
                            name="indication_operative"
                            value={form.indication_operative || ""}
                            onChange={handleChange}
                            rows="2"
                            placeholder="e.g., Failure to progress, Fetal distress, Previous 2 CS..."
                        />
                    </Form.Group>
                    {dr.isCS && (
                        <DRHint>C-Section documentation will be available after saving the delivery record.</DRHint>
                    )}
                </div>
            )}
        </div>
    );

    /* ── Section 2: Labour Stages ── */
    const renderStagesSection = () => (
        <div>
            <div
                style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}
                onClick={() => toggleSection("stages")}
            >
                <DRFormTitle>Duration of Labour Stages</DRFormTitle>
                <span style={{ fontSize: "0.8rem", color: "#999" }}>{collapsed.stages ? "▼" : "▲"}</span>
            </div>
            {!collapsed.stages && (
                <DRFormGrid cols={3}>
                    <Form.Group>
                        <Label name="First Stage (hours)" />
                        <TextField
                            type="number"
                            name="duration_first_stage_hours"
                            value={form.duration_first_stage_hours || ""}
                            onChange={handleChange}
                            step="0.5"
                            min="0"
                            placeholder="e.g., 8"
                        />
                        <DRHint>From active labour (4cm) to full dilation</DRHint>
                    </Form.Group>
                    <Form.Group>
                        <Label name="Second Stage (minutes)" />
                        <TextField
                            type="number"
                            name="duration_second_stage_minutes"
                            value={form.duration_second_stage_minutes || ""}
                            onChange={handleChange}
                            min="0"
                            placeholder="e.g., 45"
                        />
                        <DRHint>Full dilation to delivery of baby</DRHint>
                        {parseFloat(form.duration_second_stage_minutes) > 120 && (
                            <DRHint warn>Prolonged 2nd stage (&gt;2 hours)</DRHint>
                        )}
                    </Form.Group>
                    <Form.Group>
                        <Label name="Third Stage (minutes)" />
                        <TextField
                            type="number"
                            name="duration_third_stage_minutes"
                            value={form.duration_third_stage_minutes || ""}
                            onChange={handleChange}
                            min="0"
                            placeholder="e.g., 10"
                        />
                        <DRHint>Delivery of baby to placenta delivery</DRHint>
                        {parseFloat(form.duration_third_stage_minutes) > 30 && (
                            <DRHint warn>Prolonged 3rd stage (&gt;30 min). Consider retained placenta.</DRHint>
                        )}
                    </Form.Group>
                </DRFormGrid>
            )}
        </div>
    );

    /* ── Section 3: Third Stage & AMTSL ── */
    const renderThirdStageSection = () => {
        const amtslDone = dr.amtslChecklist.uterotonic && dr.amtslChecklist.cct && dr.amtslChecklist.uterine_massage;
        const amtslCount = (dr.amtslChecklist.uterotonic ? 1 : 0) +
                           (dr.amtslChecklist.cct ? 1 : 0) +
                           (dr.amtslChecklist.uterine_massage ? 1 : 0);

        return (
            <div>
                {/* AMTSL Checklist */}
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                        <DRFormTitle>AMTSL — Active Management of Third Stage</DRFormTitle>
                        <AMTSLProgress complete={amtslDone}>
                            {amtslCount}/3 {amtslDone ? "Complete" : "Incomplete"}
                        </AMTSLProgress>
                    </div>
                    <div>
                        {dr.AMTSL_ITEMS.map((item) => (
                            <AMTSLItem
                                key={item.key}
                                checked={dr.amtslChecklist[item.key]}
                                onClick={() => dr.toggleAmtsl(item.key)}
                            >
                                <AMTSLCheckIcon checked={dr.amtslChecklist[item.key]} />
                                <AMTSLLabel checked={dr.amtslChecklist[item.key]}>{item.label}</AMTSLLabel>
                            </AMTSLItem>
                        ))}
                    </div>
                </div>

                {/* Placenta & Blood Loss */}
                <div>
                    <div
                        style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}
                        onClick={() => toggleSection("thirdstage")}
                    >
                        <DRFormTitle>Placenta &amp; Blood Loss</DRFormTitle>
                        <span style={{ fontSize: "0.8rem", color: "#999" }}>{collapsed.thirdstage ? "▼" : "▲"}</span>
                    </div>
                    {!collapsed.thirdstage && (
                        <div>
                            <DRFormGrid cols={2}>
                                <Form.Group>
                                    <Label name="Placenta Complete?" />
                                    <SelectInput
                                        name="placenta_complete"
                                        value={form.placenta_complete === true || form.placenta_complete === "true" ? "true" : "false"}
                                        onChange={(ev) => {
                                            handleChange({
                                                target: { name: "placenta_complete", value: ev.target.value === "true", type: "select" },
                                            });
                                        }}
                                        options={PLACENTA_COMPLETE_OPTIONS}
                                    />
                                    {!form.placenta_complete && (
                                        <DRHint warn>Incomplete placenta — risk of retained products</DRHint>
                                    )}
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Placenta Delivery Method" />
                                    <SelectInput
                                        name="placenta_delivery_method"
                                        value={form.placenta_delivery_method || ""}
                                        onChange={handleChange}
                                        options={toSelectOptions(dr.PLACENTA_METHODS)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Estimated Blood Loss (ml)" type />
                                    <TextField
                                        type="number"
                                        name="estimated_blood_loss_ml"
                                        value={form.estimated_blood_loss_ml || ""}
                                        onChange={handleChange}
                                        placeholder="e.g., 250"
                                        min="0"
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Uterotonic Given" />
                                    <SelectInput
                                        name="uterotonic_given"
                                        value={form.uterotonic_given || ""}
                                        onChange={handleChange}
                                        options={toSelectOptions(dr.UTEROTONICS)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Uterotonic Time" />
                                    <TextField
                                        type="time"
                                        name="uterotonic_time"
                                        value={form.uterotonic_time || ""}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Label name="Cord Clamping" />
                                    <SelectInput
                                        name="cord_clamping"
                                        value={form.cord_clamping || ""}
                                        onChange={handleChange}
                                        options={toSelectOptions(dr.CORD_CLAMPING)}
                                    />
                                </Form.Group>
                            </DRFormGrid>
                            {/* PPH Alert */}
                            {dr.pphAlert && (
                                <div style={{ padding: "12px", marginTop: "12px", border: "1px solid #c62828", borderRadius: "6px", background: "#fff5f5" }}>
                                    <div style={{ fontWeight: "bold" }}>{dr.pphAlert.title}</div>
                                    <div>{dr.pphAlert.detail}</div>
                                    <div style={{ marginTop: "4px", fontStyle: "italic" }}>{dr.pphAlert.action}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    /* ── Section 4: Perineal Assessment ── */
    const renderPerinealSection = () => (
        <div>
            <div
                style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}
                onClick={() => toggleSection("perineal")}
            >
                <DRFormTitle>Perineal Assessment</DRFormTitle>
                <span style={{ fontSize: "0.8rem", color: "#999" }}>{collapsed.perineal ? "▼" : "▲"}</span>
            </div>
            {!collapsed.perineal && (
                <DRFormGrid cols={3}>
                    <Form.Group>
                        <Label name="Episiotomy" />
                        <SelectInput
                            name="episiotomy"
                            value={form.episiotomy || "none"}
                            onChange={handleChange}
                            options={toSelectOptions(dr.EPISIOTOMY_TYPES)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Perineal Tear Degree" />
                        <SelectInput
                            name="perineal_tear_degree"
                            value={form.perineal_tear_degree || "none"}
                            onChange={handleChange}
                            options={toSelectOptions(dr.TEAR_DEGREES)}
                        />
                        {(form.perineal_tear_degree === "3a" || form.perineal_tear_degree === "3b" ||
                          form.perineal_tear_degree === "3c" || form.perineal_tear_degree === "4") && (
                            <DRHint warn>Severe tear — requires experienced surgeon for repair</DRHint>
                        )}
                    </Form.Group>
                    <Form.Group>
                        <Label name="Perineal Repair Done?" />
                        <SelectInput
                            name="perineal_repair"
                            value={form.perineal_repair ? "true" : "false"}
                            onChange={(ev) => {
                                handleChange({
                                    target: { name: "perineal_repair", value: ev.target.value === "true", type: "select" },
                                });
                            }}
                            options={YES_NO_OPTIONS}
                        />
                    </Form.Group>
                </DRFormGrid>
            )}
        </div>
    );

    const memoizedToggleSection = useCallback((key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    /* ── Section 6: Maternal Condition ── */
    const renderConditionSection = () => (
        <div>
            <div
                style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}
                onClick={() => toggleSection("condition")}
            >
                <DRFormTitle>Maternal Condition Post-Delivery</DRFormTitle>
                <span style={{ fontSize: "0.8rem", color: "#999" }}>{collapsed.condition ? "▼" : "▲"}</span>
            </div>
            {!collapsed.condition && (
                <div>
                    <DRFormGrid cols={2}>
                        <Form.Group>
                            <Label name="Maternal Condition" type />
                            <SelectInput
                                name="maternal_condition_post_delivery"
                                value={form.maternal_condition_post_delivery || ""}
                                onChange={handleChange}
                                options={toSelectOptions(dr.MATERNAL_CONDITIONS)}
                                defaultOption="— Select —"
                            />
                            {form.maternal_condition_post_delivery === "critical" && (
                                <DRHint warn>CRITICAL — Activate obstetric emergency response</DRHint>
                            )}
                            {form.maternal_condition_post_delivery === "unstable" && (
                                <DRHint warn>Unstable — Close monitoring required</DRHint>
                            )}
                        </Form.Group>
                        <Form.Group>
                            <Label name="Conducted By" />
                            <TextField
                                type="text"
                                name="conducted_by"
                                value={doctorName || ""}
                                readOnly
                            />
                        </Form.Group>
                    </DRFormGrid>
                    <Form.Group style={{ marginTop: "12px" }}>
                        <Label name="Clinical Notes" />
                        <ResizableTextarea
                            name="notes"
                            value={form.notes || ""}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Summary of delivery, any notable events, post-delivery instructions..."
                        />
                    </Form.Group>
                </div>
            )}
        </div>
    );

    /* ── Main Render ── */
    return (
        <div>
            {/* All sections rendered */}
            <div id="dr-section-mode">{renderModeSection()}</div>
            <div id="dr-section-stages">{renderStagesSection()}</div>
            <div id="dr-section-thirdstage">{renderThirdStageSection()}</div>
            <div id="dr-section-perineal">{renderPerinealSection()}</div>
            <div id="dr-section-complications">
                <ComplicationsSection
                    complications={form.complications}
                    options={dr.COMPLICATION_OPTIONS}
                    toggleComplication={dr.toggleComplication}
                    collapsed={collapsed.complications}
                    toggleSection={memoizedToggleSection}
                />
            </div>
            <div id="dr-section-condition">{renderConditionSection()}</div>
            {/* Action Buttons */}
            <DRButtonRow>
                {/* <DRSecondaryBtn onClick={() => dr.setActiveTab("summary")}>View Summary</DRSecondaryBtn> */}
                <button className="btn sms-info-btn"
                    disabled={dr.saving || !form.delivery_mode}
                    onClick={dr.saveDelivery}
                >
                    {dr.saving ? "Saving..." : (dr.deliveryRecord ? "Update Delivery Record" : "Save Delivery Record")}
                </button>
            </DRButtonRow>
        </div>
    );
};
